import {
  ArrowRight,
  Building2,
  BusFront,
  Clock,
  Eye,
  Factory,
  GraduationCap,
  Gauge,
  Home,
  Hospital,
  Hotel,
  Layers,
  Maximize,
  Package,
  Settings2,
  ShieldCheck,
  ShoppingBag,
  Store,
  Trees,
  Users,
  Waves,
  Wrench,
} from 'lucide-react';
import { coreFeatures, expertise, services } from '../data/content';

// coreFeatures indexes that get their own large bento tiles.
const PCB = 3;
const PREFAB = 10;
const SUPPORT = 11;
const CAPACITY = 12;
const highlighted = [PCB, PREFAB, SUPPORT, CAPACITY];

// Icons for the remaining small tiles, keyed by coreFeatures index.
const smallIcons = { 0: Settings2, 1: Eye, 2: Clock, 4: Waves, 5: Gauge, 6: Layers, 7: Wrench, 8: Maximize, 9: Package };
const expertiseIcons = [Home, Factory, Store, Hotel, GraduationCap, Building2, Hospital, Trees, BusFront, ShoppingBag];

function SectorRow({ items, reverse }) {
  const doubled = [...items, ...items, ...items, ...items];
  return (
    <div aria-hidden="true" className="flex overflow-hidden [mask-image:linear-gradient(90deg,transparent,#000_10%,#000_90%,transparent)]">
      <div className={`marquee-track flex w-max gap-3 ${reverse ? 'animate-marquee-rev' : 'animate-marquee'}`}>
        {doubled.map(({ label, Icon }, i) => (
          <span
            key={i}
            className="flex items-center gap-3 whitespace-nowrap rounded-full bg-white py-2 pl-2 pr-5 text-sm font-medium text-ink ring-1 ring-gray-200"
          >
            <span className="rounded-full bg-brand-600 p-2 text-white">
              <Icon className="h-4 w-4" />
            </span>
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function Features() {
  const sectors = expertise.map((label, i) => ({ label, Icon: expertiseIcons[i] || Building2 }));
  const half = Math.ceil(sectors.length / 2);

  return (
    <section id="features" className="bg-white py-24 lg:py-32">
      <div className="container-x">
        <div className="reveal grid gap-6 lg:grid-cols-2 lg:items-end">
          <div>
            <span className="eyebrow">Core Features</span>
            <h2 className="section-title">
              Built to perform.
              <br />
              <span className="text-gray-400">Built to last.</span>
            </h2>
          </div>
          <p className="max-w-md text-gray-600 lg:justify-self-end">
            Every Agnisafe plant is designed around reliability, compliance and low operating
            effort — backed by a trained and experienced team.
          </p>
        </div>

        {/* bento */}
        <div className="mt-14 grid auto-rows-[minmax(9rem,auto)] gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="reveal relative overflow-hidden rounded-[2rem] bg-brand-600 p-8 text-white sm:col-span-2 lg:row-span-2">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-white/70">Capacity range</div>
            <div className="mt-4 flex items-center gap-4 font-display text-6xl font-bold sm:text-7xl">
              KLD <ArrowRight className="h-10 w-10" /> MLD
            </div>
            <p className="mt-4 max-w-sm text-white/85">{coreFeatures[CAPACITY]}</p>
            <div className="absolute inset-x-8 bottom-8 flex h-24 items-end gap-2" aria-hidden="true">
              {[18, 26, 34, 42, 52, 60, 70, 80, 90, 100].map((h, i) => (
                <span key={i} className="flex-1 rounded-t-md bg-white/25" style={{ height: `${h}%` }} />
              ))}
            </div>
            <div className="h-28" />
          </div>

          <div className="reveal group relative min-h-[16rem] overflow-hidden rounded-[2rem] sm:col-span-2">
            <img
              src={`${process.env.PUBLIC_URL}/images/stp.jpg`}
              alt="Pre-fabricated treatment plant"
              className="absolute inset-0 h-full w-full object-cover transition duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/50 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-7 text-white">
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-400">Build options</div>
              <p className="mt-2 font-display text-xl font-semibold">{coreFeatures[PREFAB]}</p>
            </div>
          </div>

          <div className="reveal flex flex-col justify-between rounded-[2rem] bg-ink p-7 text-white sm:col-span-2">
            <ShieldCheck className="h-9 w-9 text-brand-500" />
            <p className="mt-6 font-medium leading-relaxed">{coreFeatures[PCB]}</p>
          </div>

          {coreFeatures.map((f, i) => {
            if (highlighted.includes(i)) return null;
            const Icon = smallIcons[i] || Settings2;
            return (
              <div
                key={f}
                className="reveal group flex flex-col justify-between rounded-[2rem] p-6 ring-1 ring-gray-200 transition hover:bg-gray-50 hover:ring-brand-600"
              >
                <div className="flex items-center justify-between">
                  <Icon className="h-6 w-6 text-brand-600" />
                  <span className="font-display text-xs text-gray-300">{String(i + 1).padStart(2, '0')}</span>
                </div>
                <p className="mt-6 text-sm font-medium leading-relaxed text-gray-700">{f}</p>
              </div>
            );
          })}
          <div className="reveal flex flex-col justify-between gap-6 rounded-[2rem] bg-gray-100 p-7 sm:flex-row sm:items-center lg:col-span-3">
            <Users className="h-10 w-10 flex-none text-brand-600" />
            <p className="font-display text-lg font-semibold leading-snug text-ink sm:text-xl">{coreFeatures[SUPPORT]}</p>
          </div>
        </div>

        {/* services */}
        <div className="mt-28 grid gap-10 lg:grid-cols-12">
          <div className="reveal lg:col-span-4">
            <div className="lg:sticky lg:top-32">
              <span className="eyebrow">Our Services</span>
              <h3 className="section-title">From survey to O&amp;M.</h3>
              <p className="mt-4 text-gray-600">
                One partner across the entire life of your plant — engineering, execution and
                operation.
              </p>
            </div>
          </div>
          <ol className="reveal divide-y divide-gray-200 border-y border-gray-200 lg:col-span-8">
            {services.map((s, i) => (
              <li
                key={s}
                className="group flex items-center gap-6 px-2 py-5 transition hover:bg-brand-600 hover:px-6 hover:text-white sm:gap-10"
              >
                <span className="w-10 flex-none font-display text-2xl font-bold text-gray-300 transition group-hover:text-white/60">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="flex-1 font-medium">{s}</span>
                <ArrowRight className="h-5 w-5 flex-none -translate-x-2 opacity-0 transition group-hover:translate-x-0 group-hover:opacity-100" />
              </li>
            ))}
          </ol>
        </div>
      </div>

      {/* expertise marquee */}
      <div className="mt-28 bg-gray-50 py-16">
        <div className="container-x reveal mb-10 text-center">
          <span className="eyebrow">Our Expertise</span>
          <h3 className="section-title">Wherever water flows, we engineer it.</h3>
        </div>
        <div className="space-y-4">
          <SectorRow items={sectors.slice(0, half)} />
          <SectorRow items={sectors.slice(half)} reverse />
        </div>
        <ul className="sr-only">
          {expertise.map((e) => (
            <li key={e}>{e}</li>
          ))}
        </ul>
      </div>
    </section>
  );
}
